import pluralize from "pluralize";

import {
  AnyProgressBarWidgetTextReplacements,
  AnyProgressBarWidgetVariables,
  BuyMoreReplacements,
  DiscountProgressBarProgress,
  DiscountProgressBarWidgetBuildRewardSnippetOptions,
  DiscountTierDto,
  MemCartItem,
  OfferDiscountLikeObject,
  TiersUnlockedReplacements,
} from "@intelligems/ig-types";
import { ensureNumber } from "@intelligems/ig-utils";

const MAX = 999999999999999;

function getTierRewardText(options: BuyMoreReplacements) {
  if (options.textReplacements?.tierVariables) {
    return options.textReplacements.tierVariables[options.nextTier.id]
      ?.rewardText;
  }
  return undefined;
}

function getTierBuyMoreTemplate(options: BuyMoreReplacements) {
  if (options.textReplacements?.tierVariables) {
    return options.textReplacements.tierVariables[options.nextTier.id]
      ?.buyMoreTemplate;
  }
  return undefined;
}

function getTierTooltipTemplate(
  tierId: string,
  textReplacements?: AnyProgressBarWidgetTextReplacements
) {
  if (textReplacements?.tierVariables) {
    return textReplacements.tierVariables[tierId]?.tooltipTemplate;
  }
  return undefined;
}

function getCurrentTierRewardText(options: TiersUnlockedReplacements) {
  if (options.textReplacements?.tierVariables) {
    const currTier = options.discount.tiers.find(
      (tier) => tier.unitDiscount === options.unitDiscount
    );

    if (currTier) {
      return options.textReplacements.tierVariables[currTier.id]?.rewardText;
    }
  }
  return undefined;
}

function getCurrentTierCurrentUnlockTemplate(
  options: TiersUnlockedReplacements
) {
  if (options.textReplacements?.tierVariables) {
    const currTier = options.discount.tiers.find(
      (tier) => tier.id === options.currTier?.id
    );

    if (currTier) {
      return options.textReplacements.tierVariables[currTier.id]
        ?.currentUnlockedTemplate;
    }
  }
  return undefined;
}

function _standardBuyMoreReplacements(
  text: string,
  options: BuyMoreReplacements,
  formatCurrency: (price: number | string, hideCurrency?: boolean) => string
) {
  const unitName = _getUnitName(options.textReplacements);
  const more = options.textReplacements?.more || "more";

  const snippetOptions = {
    hasGwp: options.nextTier.isGiftWithPurchase,
    hasMoney: options.nextTier.unitDiscount > 0,
    hasFreeShipping: options.nextTier.isFreeShipping,
  };

  // replace #rewardText with tier variable or snippet builder
  const rewardText = getTierRewardText(options);
  text = text.replaceAll(
    "#rewardText",
    rewardText || buildBuyMoreRewardSnippet(snippetOptions)
  );

  if (options.unitType === "unit") {
    text = text.replaceAll("#quantity", options.quantityDifference.toString());

    if (options.discountType === "dollar") {
      text = text.replaceAll(
        "#discount",
        formatCurrency(options.nextTier.unitDiscount)
      );
    } else {
      text = text.replaceAll(
        "#discount",
        `${options.nextTier.unitDiscount.toString()}%`
      );
    }
  } else {
    text = text.replaceAll(
      "#quantity",
      formatCurrency(options.quantityDifference)
    );
    if (options.discountType === "dollar") {
      text = text.replaceAll(
        "#discount",
        formatCurrency(options.nextTier.unitDiscount)
      );
    } else {
      text = text.replaceAll(
        "#discount",
        `${options.nextTier.unitDiscount.toString()}%`
      );
    }
  }

  if (options.quantityDifference === 1) {
    text = text.replaceAll("#unitName", pluralize.singular(unitName));
  } else {
    text = text.replaceAll("#unitName", pluralize(unitName));
  }

  text = text.replaceAll("#more", options.isMore ? more : "");

  text = text.replaceAll("#unitName", unitName);

  return text;
}

function _buyMoreText(
  options: BuyMoreReplacements,
  formatCurrency: (price: number | string, hideCurrency?: boolean) => string
) {
  let text;
  const customTierBuyMoreTemplate = getTierBuyMoreTemplate(options);
  if (customTierBuyMoreTemplate) {
    text = customTierBuyMoreTemplate;
  } else {
    if (options.textReplacements && options.textReplacements.buyMoreTemplate) {
      text = options.textReplacements.buyMoreTemplate;
    } else {
      if (options.unitType === "unit") {
        text = "Buy #quantity #more #unitName to unlock #rewardSnippet";
      } else {
        text = "Spend #quantity to unlock #rewardSnippet";
      }
    }
    const customRewardText = getTierRewardText(options);
    if (customRewardText) {
      text = text.replaceAll("#rewardSnippet", customRewardText);
    } else {
      const snippetOptions = {
        hasGwp: options.nextTier.isGiftWithPurchase,
        hasMoney: options.nextTier.unitDiscount > 0,
        hasFreeShipping: options.nextTier.isFreeShipping,
      };
      text = text.replaceAll(
        "#rewardSnippet",
        buildBuyMoreRewardSnippet(snippetOptions)
      );
    }
  }

  return _standardBuyMoreReplacements(text, options, formatCurrency);
}

function _getUnitName(textReplacements?: AnyProgressBarWidgetTextReplacements) {
  let unitName: string;
  unitName = textReplacements?.unitName || "item";
  unitName = pluralize.isPlural(unitName)
    ? pluralize.singular(unitName)
    : unitName;

  return unitName;
}

export async function getTooltipMessage(
  discount: OfferDiscountLikeObject | null,
  tierId?: string,
  textReplacements?: AnyProgressBarWidgetTextReplacements
): Promise<string> {
  if (!tierId) {
    if (discount?.unitType === "dollar") {
      return `Spend #unit to get #rewardSnippet`;
    } else {
      return `Buy #unit #unitName to get #rewardSnippet`;
    }
  } else {
    const customTierTooltipTemplate = getTierTooltipTemplate(
      tierId,
      textReplacements
    );
    if (customTierTooltipTemplate) {
      return customTierTooltipTemplate;
    } else {
      if (textReplacements?.tooltipTemplate) {
        return textReplacements.tooltipTemplate;
      } else {
        if (discount?.unitType === "dollar") {
          return `Spend #unit to get #rewardSnippet`;
        } else {
          return `Buy #unit #unitName to get #rewardSnippet`;
        }
      }
    }
  }
}

export async function standardTiersUnlockedReplacements(
  text: string,
  options: TiersUnlockedReplacements,
  formatCurrency: (price: number | string, hideCurrency?: boolean) => string
): Promise<string> {
  const currTier = options.discount.tiers.find(
    (tier) => tier.unitDiscount === options.unitDiscount
  );
  const unitName = _getUnitName(options.textReplacements);

  if (currTier) {
    const snippetOptions = {
      hasGwp: currTier.isGiftWithPurchase,
      hasMoney: currTier.unitDiscount > 0,
      hasFreeShipping: currTier.isFreeShipping,
    };

    const rewardText = getCurrentTierRewardText(options);

    text = text.replaceAll(
      "#rewardText",
      rewardText || buildBuyMoreRewardSnippet(snippetOptions)
    );
  }

  if (options.discount.discountType === "dollar") {
    text = text.replaceAll("#discount", formatCurrency(options.unitDiscount));
  } else {
    text = text.replaceAll("#discount", options.unitDiscount.toString() + "%");
  }

  return text.replaceAll("#unitName", unitName);
}

export async function calculateMessages(
  discount: OfferDiscountLikeObject,
  cartItems: MemCartItem[],
  isEntireStore: boolean,
  formatCurrency: (price: number | string, hideCurrency?: boolean) => string,
  textReplacements?: AnyProgressBarWidgetTextReplacements,
  subtotalPrice?: number,
  afterDiscount = true
) {
  let filteredCartItems;
  if (isEntireStore) {
    filteredCartItems = cartItems;
  } else {
    filteredCartItems = cartItems.filter((item) => item.isVdProduct);
  }

  let totalPriceOrQuantity;
  if (subtotalPrice) {
    totalPriceOrQuantity = subtotalPrice;
  } else {
    if (discount.unitType === "dollar") {
      totalPriceOrQuantity = filteredCartItems.reduce((prev, cur) => {
        let price = 0;
        if (afterDiscount) {
          price = cur.discounted_price ?? cur.final_price;
        } else {
          if (cur.properties && cur.properties["_igGWP"] === undefined)
            price = cur.price;
        }
        return prev + (price ? ensureNumber(price) * cur.quantity : 0);
      }, 0);
    } else {
      totalPriceOrQuantity = filteredCartItems.reduce(
        (prev, cur) => prev + cur.quantity,
        0
      );
    }
  }

  for (let i = 0; i < discount.tiers.length; i++) {
    if (
      totalPriceOrQuantity >= discount.tiers[i].minimumUnits &&
      totalPriceOrQuantity <= discount.tiers[i].maximumUnits
    ) {
      let nextTierMin;
      if (i < discount.tiers.length - 1) {
        nextTierMin = discount.tiers[i + 1].minimumUnits;
      }

      if (nextTierMin) {
        const quantityDifference = nextTierMin - totalPriceOrQuantity;
        let getUnlocked = {
          hasMoney: false,
          hasGwp: false,
          hasFreeShipping: false,
        };
        let unlockedMessage = "";
        // eslint-disable-next-line no-await-in-loop
        await getWhatsUnlocked(discount.tiers.slice(0, i + 1)).then(
          (currUnlockedResp) => {
            getUnlocked = currUnlockedResp;
          }
        );

        // eslint-disable-next-line no-await-in-loop
        await _currentUnlockedText(
          {
            hasFreeShipping: getUnlocked.hasFreeShipping,
            hasGwp: getUnlocked.hasGwp,
            hasMoney: getUnlocked.hasMoney,
            discount: discount,
            unitDiscount: discount.tiers[i].unitDiscount,
            textReplacements,
            currTier: discount.tiers[i],
          },
          formatCurrency
        ).then((currentUnlockResp) => {
          unlockedMessage = currentUnlockResp;
        });

        return {
          message: _buyMoreText(
            {
              quantityDifference,
              nextTier: discount.tiers[i + 1],
              discountType: discount.discountType,
              isMore: true,
              unitType: discount.unitType,
              textReplacements,
            },
            formatCurrency
          ),
          unlockedMessage,
        };
      } else {
        let getUnlocked = {
          hasMoney: false,
          hasGwp: false,
          hasFreeShipping: false,
        };
        let message = "";
        // eslint-disable-next-line no-await-in-loop
        await getWhatsUnlocked(discount.tiers).then((currUnlockedResp) => {
          getUnlocked = currUnlockedResp;
        });

        // eslint-disable-next-line no-await-in-loop
        await _tiersUnlockedText(
          {
            hasFreeShipping: getUnlocked.hasFreeShipping,
            hasGwp: getUnlocked.hasGwp,
            hasMoney: getUnlocked.hasMoney,
            discount,
            allTiersUnlocked: true,
            unitDiscount: discount.tiers[i].unitDiscount,
            textReplacements,
            currTier: discount.tiers[i],
          },
          formatCurrency
        ).then((tiersUnlockedResp) => {
          message = tiersUnlockedResp;
        });
        return {
          message,
          unlockedMessage: "",
        };
      }
    }
  }

  if (totalPriceOrQuantity < discount.tiers[0].minimumUnits) {
    const quantityDifference =
      discount.tiers[0].minimumUnits - totalPriceOrQuantity;
    return {
      message: _buyMoreText(
        {
          quantityDifference: quantityDifference,
          nextTier: discount.tiers[0],
          discountType: discount.discountType,
          isMore: true,
          unitType: discount.unitType,
          textReplacements,
        },
        formatCurrency
      ),
      unlockedMessage: "",
    };
  }

  return { message: "", unlockedMessage: "" };
}

export async function getWhatsUnlocked(tiers: DiscountTierDto[]) {
  let hasMoney = false;
  let hasGwp = false;
  let hasFreeShipping = false;

  for (const tier of tiers) {
    if (tier.isGiftWithPurchase) {
      hasGwp = true;
    }
    if (tier.isFreeShipping) {
      hasFreeShipping = true;
    }
    if (tier.unitDiscount > 0) {
      hasMoney = true;
    }
  }
  return { hasMoney, hasGwp, hasFreeShipping };
}

export async function calculateProgressBarPercentage(
  discount: OfferDiscountLikeObject,
  cartItems: MemCartItem[],
  isEntireStore: boolean,
  totalPriceOrQuantityInput?: number,
  afterDiscount = true
): Promise<DiscountProgressBarProgress> {
  afterDiscount = afterDiscount ?? true;
  const percentageInterval = 1 / (discount.tiers.length - 1);

  let filteredCartItems;
  if (isEntireStore) {
    filteredCartItems = cartItems;
  } else {
    filteredCartItems = cartItems.filter((item) => item.isVdProduct);
  }

  let totalPriceOrQuantity;
  if (totalPriceOrQuantityInput) {
    totalPriceOrQuantity = totalPriceOrQuantityInput;
  } else {
    if (discount.unitType === "dollar") {
      totalPriceOrQuantity = filteredCartItems.reduce((prev, cur) => {
        let price = 0;
        if (afterDiscount) {
          price = cur.discounted_price ?? cur.final_price;
        } else {
          if (cur.properties && cur.properties["_igGWP"] === undefined)
            price = cur.price;
        }
        return prev + (price ? ensureNumber(price) * cur.quantity : 0);
      }, 0);
    } else {
      totalPriceOrQuantity = filteredCartItems.reduce(
        (prev, cur) => prev + cur.quantity,
        0
      );
    }
  }

  if (totalPriceOrQuantity < discount.tiers[0].minimumUnits) {
    return {
      section: 0,
      progress: totalPriceOrQuantity / discount.tiers[0].minimumUnits,
    };
  }

  for (let i = 0; i < discount.tiers.length; i++) {
    if (
      totalPriceOrQuantity >= discount.tiers[i].minimumUnits &&
      totalPriceOrQuantity <= discount.tiers[i].maximumUnits
    ) {
      let nextTierMin;
      if (i < discount.tiers.length - 1) {
        nextTierMin = discount.tiers[i + 1].minimumUnits;
      } else {
        nextTierMin = MAX;
      }

      const prevIntervals = percentageInterval * i;

      const nextSection =
        ((totalPriceOrQuantity - discount.tiers[i].minimumUnits) /
          (nextTierMin - discount.tiers[i].minimumUnits)) *
        percentageInterval;

      const total = (prevIntervals + nextSection) * 100;

      if (i === discount.tiers.length - 1) {
        return { section: i + 1, progress: 100 };
      }
      return { section: i + 1, progress: total > 100 ? 100 : total };
    }
  }
  return { section: 0, progress: 0 };
}

async function _tiersUnlockedText(
  options: {
    unitDiscount: number;
    discount: OfferDiscountLikeObject;
    hasGwp: boolean;
    hasMoney: boolean;
    hasFreeShipping: boolean;
    allTiersUnlocked: boolean;
    textReplacements?: AnyProgressBarWidgetTextReplacements;
    currTier?: DiscountTierDto;
  },
  formatCurrency: (price: number | string, hideCurrency?: boolean) => string
) {
  let text;
  const customLastTierReward = getCurrentTierCurrentUnlockTemplate(options);
  if (customLastTierReward) {
    text = customLastTierReward;
  } else if (
    options.textReplacements &&
    options.textReplacements.currentUnlockedTemplate
  ) {
    text = options.textReplacements.currentUnlockedTemplate;
  } else if (
    options.textReplacements &&
    options.textReplacements.allTiersUnlockedTemplate
  ) {
    text = options.textReplacements.allTiersUnlockedTemplate;
  } else {
    text = "Congratulations, you've unlocked<br>" + "#rewardSnippet";
    const customRewardText = getCurrentTierRewardText(options);

    if (customRewardText) {
      text = text.replaceAll("#rewardSnippet", customRewardText);
    } else {
      const snippetOptions = {
        hasGwp: options.hasGwp,
        hasMoney: options.hasMoney,
        hasFreeShipping: options.hasFreeShipping,
      };
      text = text.replaceAll(
        "#rewardSnippet",
        buildBuyMoreRewardSnippet(snippetOptions)
      );
    }
  }

  return standardTiersUnlockedReplacements(text, options, formatCurrency);
}

async function _currentUnlockedText(
  options: {
    unitDiscount: number;
    discount: OfferDiscountLikeObject;
    hasGwp: boolean;
    hasMoney: boolean;
    hasFreeShipping: boolean;
    textReplacements?: AnyProgressBarWidgetTextReplacements;
    currTier?: DiscountTierDto;
  },
  formatCurrency: (price: number | string, hideCurrency?: boolean) => string
) {
  let text;
  const customBuyMoreTemplate = getCurrentTierCurrentUnlockTemplate(options);
  if (customBuyMoreTemplate) {
    text = customBuyMoreTemplate;
  } else if (
    options.textReplacements &&
    options.textReplacements.currentUnlockedTemplate
  ) {
    text = options.textReplacements.currentUnlockedTemplate;
  } else {
    text = "You now have #rewardSnippet";
    const customRewardText = getCurrentTierRewardText(options);

    if (customRewardText) {
      text = text.replaceAll("#rewardSnippet", customRewardText);
    } else {
      const snippetOptions = {
        hasGwp: options.hasGwp,
        hasMoney: options.hasMoney,
        hasFreeShipping: options.hasFreeShipping,
      };
      text = text.replaceAll(
        "#rewardSnippet",
        buildBuyMoreRewardSnippet(snippetOptions)
      );
    }
  }

  return standardTiersUnlockedReplacements(text, options, formatCurrency);
}

export async function buildRewardSnippet(
  text: string,
  options: DiscountProgressBarWidgetBuildRewardSnippetOptions
) {
  const snippets: string[] = [];

  if (options.hasMoney) {
    snippets.push(`#discount off`);
  }

  if (options.hasFreeShipping) {
    snippets.push(`free shipping`);
  }
  if (options.hasGwp) {
    snippets.push(`a free gift`);
  }

  snippets.forEach((snippet, index) => {
    if (snippets.length === 1) {
      text += ` ${snippet}`;
    } else {
      if (snippets.length > 2 && index !== snippets.length - 1) {
        text += ` ${snippet},`;
      } else {
        if (index === 0) {
          text += ` ${snippet}`;
        } else if (snippets.length > 2) {
          text += ` and ${snippet}`;
        } else {
          text += ` & ${snippet}`;
        }
      }
    }
  });
  return text;
}

export function buildBuyMoreRewardSnippet(
  options: DiscountProgressBarWidgetBuildRewardSnippetOptions
) {
  let text: string = "";
  const snippets: string[] = [];
  if (options.hasMoney) {
    snippets.push(`#discount off`);
  }

  if (options.hasFreeShipping) {
    snippets.push(`free shipping`);
  }
  if (options.hasGwp) {
    snippets.push(`a free gift`);
  }

  snippets.forEach((snippet, index) => {
    if (snippets.length === 1) {
      text += `${snippet}`;
    } else {
      if (snippets.length > 2 && index !== snippets.length - 1) {
        text += ` ${snippet},`;
      } else {
        if (index === 0) {
          text += ` ${snippet}`;
        } else if (snippets.length > 2) {
          text += ` and ${snippet}`;
        } else {
          text += ` & ${snippet}`;
        }
      }
    }
  });
  return text;
}

export const setAnyProgressBarVariables = (
  variables: AnyProgressBarWidgetVariables
) => {
  const textReplacements: AnyProgressBarWidgetTextReplacements = {
    unitName: variables?.textReplacements?.unitName || "item",
    more: variables?.textReplacements?.more || "more",
    buyMoreTemplate: variables?.textReplacements?.buyMoreTemplate || "",
    allTiersUnlockedTemplate:
      variables?.textReplacements?.allTiersUnlockedTemplate || "",
    currentUnlockedTemplate:
      variables?.textReplacements?.currentUnlockedTemplate || "",
    labelTemplate: variables?.textReplacements?.labelTemplate || "",
    tooltipTemplate: variables?.textReplacements?.tooltipTemplate || "",
    tierVariables: variables?.textReplacements?.tierVariables || {},
  };

  return {
    ...variables,
    textReplacements,
  };
};
